// Copyright (c) 2020, Picsa Advisory Services (Pty) Ltd. All rights reserved.

import React, { PureComponent } from 'react'
import PropTypes from 'prop-types'
import { withRouter } from 'react-router-dom'
import { graphql, compose, Query } from 'react-apollo'
import gql from 'graphql-tag'

import {
  Table,
  Spinner,
  Warning,
  Button,
  SvgIcon,
  PageNavigationButtons,
  SpanWithData,
  Modal,
  ClientDivision,
  ClientPayrollCycle
} from '../'

import {
  decodeBase64,
  PageNavigationControl,
  getPageCursor,
  readablePhoneNumber
} from '../../utils'


class Employees extends PureComponent {
  static propTypes = {
    employerId: PropTypes.string,
    employmentType: PropTypes.string,
    employmentStatus: PropTypes.string,
    division: PropTypes.string,
    payrollcycle: PropTypes.string,
    createdAtStartDate: PropTypes.string,
    createdAtEndDate: PropTypes.string,
    search: PropTypes.string,
    recordsPerPage: PropTypes.number,
    selectableRows: PropTypes.bool,
    onSelectionChange: PropTypes.func
  }

  static defaultProps = {
    recordsPerPage: 50,
    selectableRows: false
  }

  state = {
    selectedRow: null,
    recordsPerPage: this.props.recordsPerPage,
    pageNav: new PageNavigationControl(),
    selectedRows: []
  }

  handleSelect = (identityId) => {
    // if (this.state.selectedRow === identityId) {
    //   this.setState({ selectedRow: null })
    // } else {
    //   this.setState(({ selectedRow: identityId }))
    // }
    this.props.history.push(`/os/employer/${this.props.match.params.employerId}/employees/${identityId}`)
  }

  setResultPerPage = (e) => {
    const numberOfResultsToShow = e.target.value
    this.setState(
      { recordsPerPage: numberOfResultsToShow },
      () => this.props.refetch({ recordsPerPage: numberOfResultsToShow })
    )
  }

  toggleSelectModal = () => (
    this.setState(prevState => ({ showSelectModal: !prevState.showSelectModal }))
  )

  selectEmployeesOnPage = () => {
    const employees = this.props.identities.edges
    if (this.state.selectedRows.length === employees.length ) {
      this.setState(
        // deselect all
        { selectedRows: [] },
        () => this.props.onSelectionChange([]))
    } else {
      const employeeIds = employees.map(employee => employee.node.id)
      this.setState(
        // select all row ids
        { selectedRows: employeeIds },
        () => this.props.onSelectionChange(employeeIds)
      )
    }
  }

  selectAllEmployees = (employeeIds) => {
    const identityQueryFilters = {
      'client__division__employer__id': decodeBase64(this.props.match.params.employerId)
    }

    if (this.props.search) {
      identityQueryFilters['search'] = this.props.search
    }

    if (this.props.division) {
      identityQueryFilters['client__division__id'] = decodeBase64(this.props.division)
    }

    if (this.props.payrollcycle) {
      identityQueryFilters['client__payrollcycle__id'] = decodeBase64(this.props.payrollcycle)
    }

    if (this.props.employmentType) {
      identityQueryFilters['client__employment_type'] = this.props.employmentType
    }

    if (this.props.employmentStatus) {
      identityQueryFilters['client__status'] = this.props.employmentStatus
    }

    if (this.props.createdAtStartDate) {
      identityQueryFilters['created_at__gte'] = this.props.createdAtStartDate
    }

    if (this.props.createdAtEndDate) {
      identityQueryFilters['created_at__lte'] = this.props.createdAtEndDate
    }

    this.setState(
      // select all row ids
      { selectedRows: employeeIds },
      () => this.props.onSelectionChange(employeeIds, identityQueryFilters)
    )
  }

  render() {
    if (this.props.loading) {
      return (
        <Spinner type='tail' className='slds-is-relative' />
      )
    }

    if (this.props.error) {
      return (
        <Warning className='slds-m-around_medium'>
          Error fetching employee data.
          <Button type='reset' onClick={() => this.props.refetch()} className='slds-m-left_x-small'>
            Retry?
          </Button>
        </Warning>
      )
    }

    const pageNav = this.state.pageNav
    const startCursor = this.props.identities.pageInfo.startCursor
    const totalCount = this.props.identities.totalCount

    const employees = this.props.identities.edges

    return (
      <div className={`${this.state.showSelectModal && 'inactive'} ${this.props.className}`}>
        {this.props.selectableRows &&
          <Button
            type="brand"
            disabled={this.state.selectedRows.length === 0 ||
              this.state.selectedRows.length === employees.length}
            onClick={() => (
              this.props.onSelectionChange(this.state.selectedRows)
            )}
            style={{
              position: 'fixed',
              transform: 'translateX(-120px)',
              zIndex: 3
            }}>
            Add selected
          </Button>
        }
        <Table
          columns={['Name', 'ID / Passport #', 'Cellphone', 'Division', 'Payroll cycle', 'Employee number', 'Employment type', 'Employment status']}
          rows={employees.length === 0
            ? [
                <tr>
                  <td colSpan={8}>
                    <SvgIcon
                      variant='utility'
                      symbol='info'
                      colour='light'
                      size='small'
                      className='slds-m-right_small'
                    />
                    No employees found
                  </td>
                </tr>
              ]
            : employees.map(({ node: identity }) => (
                <tr id={identity.id} onClick={() => this.handleSelect(identity.id)} className='clickable'>
                  <td>
                    {identity.fullName}
                  </td>
                  <td>
                    {identity.identityValue}
                  </td>
                  <td>
                    {identity.msisdnDefault
                      ? readablePhoneNumber(identity.msisdnDefault)
                      : '-'
                    }
                  </td>
                  <td>
                    <ClientDivision clientId={identity.client.id} />
                  </td>
                  <td>
                    <ClientPayrollCycle clientId={identity.client.id} />
                  </td>
                  <td>
                    {identity.client.employeeNumber}
                  </td>
                  <td>
                    <SpanWithData
                      forField='ClientEmploymentType'
                      searchKey={identity.client.employmentType}
                      loadingComponent={<Spinner size='x-small' />}
                    />
                  </td>
                  <td>
                    <SpanWithData
                      forField='ClientStatus'
                      searchKey={identity.client.status}
                      loadingComponent={<Spinner size='x-small' />}
                    />
                  </td>
                </tr>
              ))
              // <EmployeeTableRow
              //   identity={identity}
              //   selected={this.state.selectedRow !== null && this.state.selectedRow === identity.id}
              //   onClick={() => this.handleSelect(identity.id)}
              //   key={`row_${identity.id}`}
              // />
          }
          objectRows={true}
          fixedHeader={true}
          selectableRows={this.props.selectableRows && employees.length > 0}
          selectedRows={this.state.selectedRows}
          onGlobalSelect={this.toggleSelectModal}
          onRowSelect={e => {
            const id = e.target.id.split('-')[1]  // 'checkbox-' removed from id (see Checkbox)
            this.setState(prevState => {
              if (prevState.selectedRows.includes(id)) {
                const selectedRows = [...prevState.selectedRows]
                selectedRows.splice(selectedRows.indexOf(id), 1)  // selected rows minus deselected
                return { selectedRows }
              } else {
                return { selectedRows: [...prevState.selectedRows, id] }
              }
            })
          }}
          className='slds-scrollable_x'
        />
        {employees.length > 0 &&
          <PageNavigationButtons
            onNextClick={() => pageNav.nextPage(this.props.fetchNext)}
            onPreviousClick={() => pageNav.previousPage(this.props.fetchPrevious)}
            previousDisabled={pageNav.firstPage}
            nextDisabled={pageNav.lastPage}
            startCursor={startCursor}
            totalCount={totalCount}
            resultsPerPage={this.state.recordsPerPage}
            resultsText='employee(s)'
            onPageNumberClick={pageNumber => this.props.refetch({ cursor: getPageCursor(pageNumber, this.state.recordsPerPage) })}
            onResultsPerPageChange={this.setResultPerPage}
            className="slds-p-around_medium"
          />
        }
        <Query
          query={gql`
            query($employerId: UUID!, $divisionId: UUID, $payrollcycleId: UUID,  $employmentType: String, $employmentStatus: String, $search: String, $createdAtStartDate: DateTime, $createdAtEndDate: DateTime) {
              identities(client_Division_Employer_Id: $employerId, client_Division_Id: $divisionId, client_Payrollcycle_Id: $payrollcycleId, client_EmploymentType: $employmentType, client_Status: $employmentStatus, search: $search, createdAt_Gte: $createdAtStartDate, createdAt_Lte: $createdAtEndDate) {
                edges {
                  node {
                    id
                  }
                }
              }
            }
          `}
          variables={{
            search: this.props.search,
            employerId: decodeBase64(this.props.match.params.employerId),
            divisionId: decodeBase64(this.props.division),
            employmentType: this.props.employmentType,
            employmentStatus: this.props.employmentStatus,
            createdAtStartDate: this.props.createdAtStartDate,
            createdAtEndDate: this.props.createdAtEndDate
          }}>
          {({ loading, error, data }) => {
            if (this.state.showSelectModal) {
              return (
                <Modal
                  body={
                    <div>
                      <div className='u-font-size-large slds-m-bottom_large slds-align_absolute-center'>
                        <SvgIcon
                          variant='standard'
                          symbol='question_feed'
                          size='large'
                          colour='light'
                          className='slds-p-right_x-small'
                        />
                        {`Select records on this page only or all ${this.props.identities.totalCount}?`}
                      </div>
                      <div className='slds-align_absolute-center'>
                        <Button onClick={this.selectEmployeesOnPage}>
                          Add selected page records
                        </Button>
                        <Button
                          disabled={loading || error}
                          onClick={() => {
                            if (data.identities) {
                              this.selectAllEmployees(
                                data.identities.edges.map(({ node: identity }) => identity.id)
                              )
                            }
                          }}>
                          {`Select & add all ${this.props.identities.totalCount}`}
                          {loading && <Spinner type='brand' size='x-small' />}
                        </Button>
                      </div>
                    </div>
                  }
                  contentClassName='modal-box-shadow-larger'
                  buttonIconInverse={false}
                  onCloseClick={this.toggleSelectModal}
                />
              )
            }
            else {
              return null
            }
          }}
        </Query>
      </div>
    )
  }
}

const getEmployees = gql`
  query($employerId: UUID!, $divisionId: UUID, $payrollcycleId: UUID, $employmentType: String, $employmentStatus: String, $search: String, $createdAtStartDate: DateTime, $createdAtEndDate: DateTime, $orderBy: String, $cursor: String, $recordsPerPage: Int) {
    identities(client_Division_Employer_Id: $employerId, client_Division_Id: $divisionId, client_Payrollcycle_Id: $payrollcycleId, client_EmploymentType: $employmentType, client_Status: $employmentStatus, search: $search, createdAt_Gte: $createdAtStartDate, createdAt_Lte: $createdAtEndDate, orderBy: $orderBy, first: $recordsPerPage, after: $cursor) {
      totalCount
      edges {
        node {
          id
          firstName
          fullName
          identityValue
          msisdnDefault
          client {
            id
            employeeNumber
            employmentType
            status
          }
        }
      }
      pageInfo {
        endCursor
        startCursor
        hasNextPage
        hasPreviousPage
      }
    }
  }
`

const getPreviousEmployees = gql`
  query($employerId: UUID!, $divisionId: UUID, $payrollcycleId: UUID, $employmentType: String, $employmentStatus: String, $search: String, $createdAtStartDate: DateTime, $createdAtEndDate: DateTime, $orderBy: String, $cursor: String, $recordsPerPage: Int) {
    identities(client_Division_Employer_Id: $employerId, client_Division_Id: $divisionId, client_Payrollcycle_Id: $payrollcycleId, client_EmploymentType: $employmentType, client_Status: $employmentStatus, search: $search, createdAt_Gte: $createdAtStartDate, createdAt_Lte: $createdAtEndDate, orderBy: $orderBy, last: $recordsPerPage, before: $cursor) {
      totalCount
      edges {
        node {
          id
          fullName
          identityValue
          msisdnDefault
          client {
            id
            employeeNumber
            employmentType
            status
          }
        }
      }
      pageInfo {
        endCursor
        startCursor
        hasNextPage
        hasPreviousPage
      }
    }
  }
`

const withData =
  graphql(getEmployees, {
    options: ({ employerId, search, division, payrollcycle, employmentType, employmentStatus, createdAtStartDate, createdAtEndDate, recordsPerPage, orderBy, cursor }) => ({
      variables: {
        search,
        employerId: decodeBase64(employerId),
        divisionId: division && division.length > 0
          ? decodeBase64(division)
          : undefined,
        payrollcycleId: payrollcycle && payrollcycle.length > 0
          ? decodeBase64(payrollcycle)
          : undefined,
        employmentType,
        employmentStatus,
        createdAtStartDate,
        createdAtEndDate,
        orderBy: orderBy || 'last_name',
        recordsPerPage: recordsPerPage || 50,
        cursor
      },
      fetchPolicy: 'network-only'
    }),
    props: ({ ownProps: { employerId, search, division, payrollcycle, employmentType, employmentStatus, createdAtStartDate, createdAtEndDate, recordsPerPage, orderBy }, data: { loading, error, refetch, identities, fetchMore } }) => ({
      loading,
      error,
      refetch,
      identities,
      fetchNext: () => {
        return fetchMore({
          query: getEmployees,
          variables: {
            search,
            employerId: decodeBase64(employerId),
            divisionId: division && division.length > 0
              ? decodeBase64(division)
              : undefined,
            payrollcycleId: payrollcycle && payrollcycle.length > 0
              ? decodeBase64(payrollcycle)
              : undefined,
            employmentType,
            employmentStatus,
            createdAtStartDate,
            createdAtEndDate,
            orderBy: orderBy || 'last_name',
            recordsPerPage: recordsPerPage || 50,
            cursor: identities.pageInfo.endCursor
          },
          updateQuery: (previousResult, { fetchMoreResult }) => {
            const newEdges = fetchMoreResult.identities.edges
            const pageInfo = fetchMoreResult.identities.pageInfo
            return {
              identities: {
                __typename: previousResult.identities.__typename,
                edges: [...newEdges],
                pageInfo
              }
            }
          }
        })
      },
      fetchPrevious: () => {
        return fetchMore({
          query: getPreviousEmployees,
          variables: {
            search,
            employerId: decodeBase64(employerId),
            divisionId: division && division.length > 0
              ? decodeBase64(division)
              : undefined,
            payrollcycleId: payrollcycle && payrollcycle.length > 0
              ? decodeBase64(payrollcycle)
              : undefined,
            employmentType,
            employmentStatus,
            createdAtStartDate,
            createdAtEndDate,
            orderBy: orderBy || 'last_name',
            recordsPerPage: recordsPerPage || 50,
            cursor: identities.pageInfo.startCursor
          },
          updateQuery: (previousResult, { fetchMoreResult }) => {
            const newEdges = fetchMoreResult.identities.edges
            const pageInfo = fetchMoreResult.identities.pageInfo
            return {
              identities: {
                __typename: previousResult.identities.__typename,
                edges: [...newEdges],
                pageInfo
              }
            }
          }
        })
      }
    })
  })

export default compose(
  withRouter,
  withData
)(Employees)



// WEBPACK FOOTER //
// ./src/components/employees/index.js